/**
 * Rounding and adjustment utilities for capital allowance calculations
 */

/**
 * Round a number to the nearest dollar
 * 
 * @param {number} value - Value to round
 * @returns {number} Rounded value
 */
export function roundToNearestDollar(value) {
  return Math.round(value);
}

/**
 * Calculate adjustment for YA totals to reconcile with asset cost
 * 
 * @param {Array} yaGroups - Array of YA groups with principalPaid
 * @param {number} assetCost - Total asset cost
 * @returns {Object} Adjustment metadata
 */
export function calculateAdjustment(yaGroups, assetCost) {
  // Round each YA total to nearest dollar
  const roundedGroups = yaGroups.map(group => ({
    ...group,
    roundedPrincipal: roundToNearestDollar(group.principalPaid)
  }));
  
  // Calculate total rounded principal
  const totalRoundedPrincipal = roundedGroups.reduce(
    (sum, group) => sum + group.roundedPrincipal,
    0
  );
  
  // Calculate difference
  const difference = Math.abs(totalRoundedPrincipal - assetCost);
  
  // Adjustment logic
  if (difference < 2) {
    // Auto-adjust final YA
    if (roundedGroups.length > 0) {
      const lastGroup = roundedGroups[roundedGroups.length - 1];
      const adjustment = assetCost - (totalRoundedPrincipal - lastGroup.roundedPrincipal);
      lastGroup.adjustedPrincipal = roundToNearestDollar(adjustment);
      lastGroup.adjustmentApplied = true;
      lastGroup.adjustmentAmount = lastGroup.adjustedPrincipal - lastGroup.roundedPrincipal;
    }
    
    return {
      adjusted: true,
      warning: false,
      difference,
      originalTotal: totalRoundedPrincipal,
      adjustedTotal: assetCost,
      roundedGroups
    };
  } else {
    // Difference >= $2, flag warning but don't auto-adjust
    return {
      adjusted: false,
      warning: true,
      difference,
      originalTotal: totalRoundedPrincipal,
      adjustedTotal: totalRoundedPrincipal,
      roundedGroups
    };
  }
}

/**
 * Apply rounding to YA groups and calculate adjustments
 * 
 * @param {Array} yaGroups - Array of YA groups
 * @param {number} assetCost - Total asset cost
 * @returns {Object} { roundedGroups: Array, adjustment: Object }
 */
export function processYARounding(yaGroups, assetCost) {
  const adjustment = calculateAdjustment(yaGroups, assetCost);
  
  // Use adjusted principal if adjustment was applied
  const finalGroups = adjustment.roundedGroups.map(group => ({
    ...group,
    principalPaid: group.adjustedPrincipal !== undefined 
      ? group.adjustedPrincipal 
      : group.roundedPrincipal
  }));
  
  return {
    roundedGroups: finalGroups,
    adjustment
  };
}

